import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { Download, MoreHorizontal, Plus } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { AdminAttendanceDialog } from '@/pages/attendance/punch-dialog'
import { CloseAttendanceDialog } from '@/pages/attendance/close-dialog'
import { PunchInfoDialog } from '@/pages/attendance/punch-info-dialog'
import { AttendanceRequestDialog } from '@/pages/attendance/request-dialog'
import { WorkerProfileLink } from '@/pages/attendance/worker-link'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { getErrorMessage } from '@/lib/api'
import { locationsApi } from '@/services/locations'
import { toTenantDayKey, useTenantTimeZone } from '@/lib/tenant-date-time'
import { workersApi, type WorkerAttendanceRequestPayload } from '@/services/workers'
import {
  formatClock,
  formatDay,
  geofenceFlag,
  methodLabel,
  monthRange,
  presetRange,
  punchDeviceLabel,
  punchDate,
  punchDisplayValue,
  punchStatus,
  punchWorkerId,
  todayIsoDate,
  useAttendanceDirectory,
  useAttendanceOrgTimeZone,
  useAttendancePunchActions,
  useAttendancePunches,
  workedLabel,
  workerDepartment,
  workerName,
  type AttendancePunch,
  type PunchStatus,
  type RangePreset,
} from '@/pages/attendance/data'

const RANGE_PRESETS: [RangePreset, string][] = [
  ['today', 'Today'],
  ['yesterday', 'Yesterday'],
  ['this-week', 'This week'],
  ['last-7', 'Last 7 days'],
  ['this-month', 'This month'],
  ['last-month', 'Last month'],
  ['month', 'Pick a month'],
  ['custom', 'Custom range'],
]

const STATUS_LABEL: Record<PunchStatus, string> = {
  complete: 'Complete',
  working: 'Working',
  missing: 'Missing punch',
  absent: 'Not clocked in',
}

function validIsoDate(value: string | null): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const parsed = new Date(`${value}T00:00:00Z`)
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
}

export default function TimeLogsPage() {
  const [searchParams] = useSearchParams()
  const orgTimeZone = useAttendanceOrgTimeZone()
  const today = todayIsoDate(orgTimeZone)
  const requestedStartDate = searchParams.get('start_date')
  const requestedEndDate = searchParams.get('end_date')
  const hasRequestedRange = validIsoDate(requestedStartDate)
    && validIsoDate(requestedEndDate)
    && requestedStartDate <= requestedEndDate
  // Display only; date filters and status use the org day above, the request dialog keeps its existing values.
  const attendanceTimeZone = useTenantTimeZone()
  const [preset, setPreset] = useState<RangePreset>(hasRequestedRange ? 'custom' : 'today')
  const [pickedMonth, setPickedMonth] = useState<string | null>(null)
  const [pickedStartDate, setStartDate] = useState<string | null>(hasRequestedRange ? requestedStartDate : null)
  const [pickedEndDate, setEndDate] = useState<string | null>(hasRequestedRange ? requestedEndDate : null)
  const month = pickedMonth ?? today.slice(0, 7)
  const range = preset === 'month'
    ? monthRange(month)
    : presetRange(preset, today) ?? { start: pickedStartDate ?? today, end: pickedEndDate ?? today }
  const startDate = range.start
  const endDate = range.end
  const choosePreset = (next: RangePreset) => {
    if (next === 'custom') {
      setStartDate(startDate)
      setEndDate(endDate)
    }
    if (next === 'month') setPickedMonth(startDate.slice(0, 7))
    setPreset(next)
  }
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<StatusFilter>('all')
  const [locationFilter, setLocationFilter] = useState(() => searchParams.get('location_id') || 'all')
  const [checkedInOnly, setCheckedInOnly] = useState(() => searchParams.get('checked_in_only') === 'true')
  const [departmentFilter, setDepartmentFilter] = useState('all')
  const [manualOpen, setManualOpen] = useState(false)
  const [requestPunch, setRequestPunch] = useState<AttendancePunch | null>(null)
  const [infoPunch, setInfoPunch] = useState<AttendancePunch | null>(null)
  const [closePunch, setClosePunch] = useState<AttendancePunch | null>(null)
  const queryClient = useQueryClient()
  const { workers, byId, isLoading, error } = useAttendanceDirectory()
  const punchesQuery = useAttendancePunches(startDate, endDate)
  const locationsQuery = useQuery({
    queryKey: ['attendance-live-locations'],
    queryFn: () => locationsApi.list(),
  })
  const requestsQuery = useQuery({
    queryKey: ['attendance-requests', 'pending'],
    queryFn: () => workersApi.getAdminAttendanceRequests({ status: 'pending' }),
  })
  const { record } = useAttendancePunchActions()
  const createRequest = useMutation({
    mutationFn: (payload: WorkerAttendanceRequestPayload) => workersApi.createAdminAttendanceRequest(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['attendance-requests'] })
    },
  })
  const punches = (punchesQuery.data?.results ?? []) as AttendancePunch[]
  const pendingAttendanceIds = useMemo(() => new Set(
    (requestsQuery.data ?? [])
      .filter((request) => request.status === 'pending' && request.attendance != null)
      .map((request) => String(request.attendance)),
  ), [requestsQuery.data])
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000)
    return () => window.clearInterval(timer)
  }, [])

  const mappedRows = useMemo(() => punches.map((punch) => {
    const worker = byId.get(punchWorkerId(punch)) ?? null
    const rowStatus = punchStatus(punch, today)
    const pendingRequest = pendingAttendanceIds.has(String(punch.id))
    return {
      punch,
      worker,
      status: rowStatus,
      pendingRequest,
      attention: rowStatus === 'missing' || Boolean(geofenceFlag(punch)) || pendingRequest,
      department: workerDepartment(worker),
    }
  }), [byId, pendingAttendanceIds, punches, today])

  const locationOptions = useMemo(() => {
    const options = new Map<string, string>()
    for (const location of locationsQuery.data ?? []) {
      if (location.deleted || location.active === false) continue
      options.set(String(location.id), location.place || `Location ${location.id}`)
    }
    for (const row of mappedRows) {
      const id = row.punch.attendance_location_id
      const name = row.punch.attendance_location_name
      if (id != null && name && !options.has(String(id))) options.set(String(id), name)
    }
    return [...options.entries()].sort((left, right) => left[1].localeCompare(right[1]))
  }, [locationsQuery.data, mappedRows])

  const departmentOptions = useMemo(() => {
    const names = new Set<string>()
    for (const worker of workers) {
      const name = workerDepartment(worker)
      if (name && name !== '—') names.add(name)
    }
    for (const row of mappedRows) {
      if (row.department && row.department !== '—') names.add(row.department)
    }
    return [...names].sort((left, right) => left.localeCompare(right))
  }, [mappedRows, workers])

  const rows = useMemo(() => mappedRows.filter((row) => {
    const name = `${workerName(row.worker)} ${row.worker?.worker_code || ''}`.toLowerCase()
    if (search.trim() && !name.includes(search.trim().toLowerCase())) return false
    if (status === 'attention' ? !row.attention : status !== 'all' && row.status !== status) return false
    if (locationFilter !== 'all' && String(row.punch.attendance_location_id ?? '') !== locationFilter) return false
    if (departmentFilter !== 'all' && row.department !== departmentFilter) return false
    if (checkedInOnly && !row.punch.check_in) return false
    return true
  }), [checkedInOnly, departmentFilter, locationFilter, mappedRows, search, status])

  const working = rows.filter((row) => row.status === 'working').length
  const attentionRows = rows.filter((row) => row.attention)
  const attentionDetail = [
    [attentionRows.filter((row) => row.status === 'missing').length, 'missing'],
    [attentionRows.filter((row) => geofenceFlag(row.punch)).length, 'outside geofence'],
    [attentionRows.filter((row) => row.pendingRequest).length, 'pending request'],
  ].filter(([count]) => Number(count) > 0).map(([count, label]) => `${count} ${label}`).join(' · ')
  const runningRows = rows.filter((row) => row.status === 'working')
  const minutes = rows.reduce((sum, row) => sum + workedMinutes(row.punch, row.status === 'working', now), 0)

  const exportCsv = () => {
    const header = ['Employee', 'Date', 'Department', 'Clock in', 'Clock out', 'Duration', 'Source', 'Status']
    const body = rows.map((row) => [
      workerName(row.worker),
      (attendanceTimeZone ? toTenantDayKey(punchDisplayValue(row.punch.check_in, row.punch.check_in_local, attendanceTimeZone), attendanceTimeZone) : null) ?? punchDate(row.punch),
      workerDepartment(row.worker),
      formatClock(punchDisplayValue(row.punch.check_in, row.punch.check_in_local, attendanceTimeZone), attendanceTimeZone),
      formatClock(punchDisplayValue(row.punch.check_out, row.punch.check_out_local, attendanceTimeZone), attendanceTimeZone),
      workedLabel(row.punch),
      methodLabel(row.punch.source),
      STATUS_LABEL[row.status],
    ])
    const csv = [header, ...body].map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `time-logs-${startDate}-to-${endDate}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-5 pb-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Time Logs</h1>
          <p className="mt-1 text-sm text-slate-500">Review punches, fix exceptions and keep employee hours accurate.</p>
        </div>
        <div className="flex gap-2">
          {locationFilter !== 'all' ? (
            <Button asChild type="button" variant="outline">
              <Link to={`/job/attendance?${new URLSearchParams({ location_id: locationFilter, date: startDate })}`}>
                Daily roster
              </Link>
            </Button>
          ) : null}
          <Button type="button" variant="outline" onClick={exportCsv}>
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
          <Button type="button" onClick={() => setManualOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Manual Punch
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <Stat label="Punches" value={String(rows.length)} detail="In the selected range" />
        <Stat label="Currently working" value={String(working)} detail="Open punches" />
        <Stat
          label="Needs attention"
          value={String(attentionRows.length)}
          detail={attentionDetail || 'Nothing to review'}
          active={status === 'attention'}
          onClick={() => setStatus(status === 'attention' ? 'all' : 'attention')}
        />
        <Stat
          label="Total hours"
          value={hoursLabel(minutes)}
          detail={runningRows.length ? `Includes ${runningRows.length} open punch${runningRows.length === 1 ? '' : 'es'} so far` : 'From completed punches'}
        />
      </div>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap gap-2 border-b border-slate-100 p-3">
          <Input className="max-w-xs" placeholder="Search employee or ID..." value={search} onChange={(event) => setSearch(event.target.value)} />
          <Select value={preset} onValueChange={(value) => choosePreset(value as RangePreset)}>
            <SelectTrigger className="w-[160px]" aria-label="Period"><SelectValue /></SelectTrigger>
            <SelectContent>
              {RANGE_PRESETS.map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {preset === 'month' && (
            <Input
              type="month"
              className="w-[170px]"
              aria-label="Month"
              value={month}
              max={today.slice(0, 7)}
              onChange={(event) => setPickedMonth(event.target.value || today.slice(0, 7))}
            />
          )}
          {preset === 'custom' && (
            <>
              <Input
                type="date"
                className="w-[160px]"
                aria-label="From date"
                value={startDate}
                max={endDate}
                onChange={(event) => {
                  const next = event.target.value || today
                  setStartDate(next)
                  if (next > endDate) setEndDate(next)
                }}
              />
              <Input
                type="date"
                className="w-[160px]"
                aria-label="To date"
                value={endDate}
                min={startDate}
                onChange={(event) => {
                  const next = event.target.value || today
                  setEndDate(next)
                  if (next < startDate) setStartDate(next)
                }}
              />
            </>
          )}
          {preset !== 'custom' && preset !== 'today' && preset !== 'yesterday' && (
            <span className="self-center text-xs text-slate-500">{formatDay(`${startDate}T00:00+00:00`)} – {formatDay(`${endDate}T00:00+00:00`)}</span>
          )}
          <Select value={locationFilter} onValueChange={setLocationFilter}>
            <SelectTrigger className="w-[200px]" aria-label="Location">
              <SelectValue placeholder="All locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All locations</SelectItem>
              {locationOptions.map(([id, name]) => (
                <SelectItem key={id} value={id}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {checkedInOnly ? (
            <Button type="button" size="sm" variant="default" onClick={() => setCheckedInOnly(false)}>
              Checked-in workers only
            </Button>
          ) : null}
          <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
            <SelectTrigger className="w-[200px]" aria-label="Department">
              <SelectValue placeholder="All departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All departments</SelectItem>
              {departmentOptions.map((name) => (
                <SelectItem key={name} value={name}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={(value) => setStatus(value as StatusFilter)}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
              <SelectItem value="working">Working</SelectItem>
              <SelectItem value="missing">Missing punch</SelectItem>
              <SelectItem value="attention">Needs attention</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {isLoading || punchesQuery.isLoading ? (
          <div className="space-y-2 p-4">{Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-12 w-full" />)}</div>
        ) : error || punchesQuery.error ? (
          <p className="p-6 text-sm text-destructive">{getErrorMessage(error || punchesQuery.error)}</p>
        ) : rows.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-500">No punches match these filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  {['Employee', 'Date', 'Department', 'Clock in', 'Clock out', 'Duration', 'Source', 'Status', ''].map((heading) => (
                    <th key={heading} className="px-4 py-3 font-medium">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.punch.id} className="border-t border-slate-100">
                    <td className="px-4 py-3">
                      <WorkerProfileLink workerId={row.worker?.worker_id ?? row.worker?.id ?? punchWorkerId(row.punch)} name={workerName(row.worker)} className="font-medium text-slate-900" />
                      <div className="text-xs text-slate-500">{row.worker?.worker_code || punchWorkerId(row.punch)}</div>
                    </td>
                    <td className="px-4 py-3">{formatDay(punchDisplayValue(row.punch.check_in, row.punch.check_in_local, attendanceTimeZone), attendanceTimeZone)}</td>
                    <td className="px-4 py-3 text-slate-600">{workerDepartment(row.worker)}</td>
                    <td className="px-4 py-3 tabular-nums">{formatClock(punchDisplayValue(row.punch.check_in, row.punch.check_in_local, attendanceTimeZone), attendanceTimeZone)}</td>
                    <td className="px-4 py-3 tabular-nums">{formatClock(punchDisplayValue(row.punch.check_out, row.punch.check_out_local, attendanceTimeZone), attendanceTimeZone)}</td>
                    <td className="px-4 py-3 font-medium">{workedLabel(row.punch)}</td>
                    <td className="px-4 py-3">
                      <div>{methodLabel(row.punch.source)}</div>
                      {punchDeviceLabel(row.punch) ? (
                        <div className="text-[10px] leading-tight text-slate-500">{punchDeviceLabel(row.punch)}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${row.status === 'complete' ? 'bg-emerald-50 text-emerald-700' : row.status === 'working' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`}>
                          {STATUS_LABEL[row.status]}
                        </span>
                        {geofenceFlag(row.punch) ? (
                          <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700" title={geofenceFlag(row.punch)?.title}>
                            {geofenceFlag(row.punch)?.label}
                          </span>
                        ) : null}
                        {row.pendingRequest ? (
                          <span className="rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700">Request pending</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-2 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label="Time log actions">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {!row.punch.check_out ? (
                            <DropdownMenuItem onClick={() => setClosePunch(row.punch)}>
                              Close attendance
                            </DropdownMenuItem>
                          ) : null}
                          <DropdownMenuItem onClick={() => setInfoPunch(row.punch)}>
                            Info
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={pendingAttendanceIds.has(String(row.punch.id))}
                            onClick={() => setRequestPunch(row.punch)}
                          >
                            {pendingAttendanceIds.has(String(row.punch.id)) ? 'Request pending' : 'Raise request'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <AdminAttendanceDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        workers={workers}
        isPending={record.isPending}
        onSubmit={async (payload) => {
          const result = await record.mutateAsync(payload)
          const note = (result as { punch_note?: string; punch_applied?: boolean } | undefined)?.punch_note
          if (note && (result as { punch_applied?: boolean })?.punch_applied === false) {
            throw new Error(note)
          }
          toast.success(payload.status ? 'Check-in recorded' : 'Check-out recorded')
        }}
      />
      <CloseAttendanceDialog
        punch={closePunch}
        employeeName={workerName(closePunch ? byId.get(punchWorkerId(closePunch)) : null)}
        timeZone={orgTimeZone}
        isPending={record.isPending}
        onOpenChange={(open) => { if (!open) setClosePunch(null) }}
        onSubmit={async (checkOut) => {
          if (!closePunch) return
          const result = await record.mutateAsync({ workerId: punchWorkerId(closePunch), status: false, timestamp: checkOut })
          const outcome = result as { punch_note?: string; punch_applied?: boolean } | undefined
          if (outcome?.punch_applied === false) throw new Error(outcome.punch_note || 'Attendance was not closed')
          toast.success('Attendance closed')
        }}
      />
      <PunchInfoDialog
        open={Boolean(infoPunch)}
        onOpenChange={(open) => { if (!open) setInfoPunch(null) }}
        punch={infoPunch}
        employeeName={workerName(infoPunch ? byId.get(punchWorkerId(infoPunch)) : null)}
        timeZone={attendanceTimeZone}
      />
      <AttendanceRequestDialog
        key={requestPunch?.id ?? 'none'}
        open={Boolean(requestPunch)}
        onOpenChange={(open) => { if (!open) setRequestPunch(null) }}
        employeeName={workerName(requestPunch ? byId.get(punchWorkerId(requestPunch)) : null)}
        workerId={requestPunch ? punchWorkerId(requestPunch) : ''}
        workDate={requestPunch ? punchDate(requestPunch) : ''}
        currentCheckIn={requestPunch ? formatClock(requestPunch.check_in_local || requestPunch.check_in) : undefined}
        currentCheckOut={requestPunch && (requestPunch.check_out || requestPunch.check_out_local) ? formatClock(requestPunch.check_out_local || requestPunch.check_out) : undefined}
        isPending={createRequest.isPending}
        onSubmit={async (payload) => {
          await createRequest.mutateAsync({
            worker_id: payload.workerId,
            attendance_id: requestPunch?.id,
            work_date: payload.workDate,
            request_type: payload.requestType,
            requested_check_in: payload.requestedCheckIn,
            requested_check_out: payload.requestedCheckOut,
            reason: payload.reason,
          })
          toast.success(payload.requestType === 'missing_punch' ? 'Missing punch request raised' : 'Time correction request raised')
        }}
      />
    </div>
  )
}

type StatusFilter = 'all' | 'attention' | PunchStatus

function hoursLabel(minutes: number) {
  const hours = Math.floor(minutes / 60)
  const rest = Math.round(minutes % 60)
  return rest ? `${hours}h ${rest}m` : `${hours}h`
}

/** Recorded minutes, or time so far for a punch that is still open today. */
function workedMinutes(punch: AttendancePunch, running: boolean, now: number) {
  if (!running) return Number(punch.total_minutes || 0)
  const started = Date.parse(String(punch.check_in || ''))
  return Number.isNaN(started) ? 0 : Math.max(Math.floor((now - started) / 60_000), 0)
}

function Stat({ label, value, detail, active, onClick }: { label: string; value: string; detail: string; active?: boolean; onClick?: () => void }) {
  const body = (
    <>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-semibold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500">{detail}</div>
    </>
  )
  if (!onClick) {
    return <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">{body}</div>
  }
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`rounded-2xl border bg-white px-4 py-3 text-left shadow-sm transition-colors hover:border-amber-300 ${active ? 'border-amber-400 ring-2 ring-amber-100' : 'border-slate-200'}`}
    >
      {body}
    </button>
  )
}
